/**
 * OutputsPane Component
 *
 * Displays aggregated outputs from all steps in a plan.
 * Allows filtering, grouping, and searching through collected information.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Link,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CodeIcon from '@mui/icons-material/Code';
import { Plan, PlanPhase } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';
import {
  aggregatePlanOutputs,
  groupOutputsByPhase,
  filterOutputsBySource,
  filterOutputsByType,
  searchOutputs,
  getOutputStats,
  OutputEntry,
  OutputSource,
  OutputType,
} from '../../utils/outputAggregator';

// ============================================================================
// Types
// ============================================================================

type GroupByValue = 'phase' | 'step' | 'type';
type FilterValue = 'all' | OutputSource | OutputType;

interface OutputsPaneProps {
  plan: Plan;
  phases: PlanPhase[];
  onNavigateToStep?: (stepId: string) => void;
}

// ============================================================================
// Helper Components
// ============================================================================

const SourceChip: React.FC<{ source: OutputSource }> = ({ source }) => {
  const config = {
    automation: {
      icon: <SmartToyIcon sx={{ fontSize: '10px !important' }} />,
      label: 'Auto',
      bgcolor: colors.blue[50],
      color: colors.blue[700],
    },
    manual: {
      icon: <PersonIcon sx={{ fontSize: '10px !important' }} />,
      label: 'Manual',
      bgcolor: colors.green[50],
      color: colors.green[700],
    },
    intake: {
      icon: <QuestionAnswerIcon sx={{ fontSize: '10px !important' }} />,
      label: 'Intake',
      bgcolor: colors.purple[50],
      color: colors.purple[600],
    },
    assessment: {
      icon: <FactCheckIcon sx={{ fontSize: '10px !important' }} />,
      label: 'Assessment',
      bgcolor: colors.amber[50],
      color: colors.amber[600],
    },
  }[source];

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.65rem',
        bgcolor: config.bgcolor,
        color: config.color,
        '& .MuiChip-icon': { color: config.color, ml: 0.5 },
      }}
    />
  );
};

const TypeIcon: React.FC<{ type: OutputType }> = ({ type }) => {
  const icons: Record<OutputType, React.ReactNode> = {
    research: <TravelExploreIcon sx={{ fontSize: 16, color: colors.blue[600] }} />,
    email: <EmailIcon sx={{ fontSize: 16, color: colors.purple[600] }} />,
    schedule: <EventIcon sx={{ fontSize: 16, color: colors.green[600] }} />,
    calculation: <CalculateIcon sx={{ fontSize: 16, color: colors.amber[600] }} />,
    document: <DescriptionIcon sx={{ fontSize: 16, color: colors.red[600] }} />,
    intake: <QuestionAnswerIcon sx={{ fontSize: 16, color: colors.purple[600] }} />,
    assessment: <FactCheckIcon sx={{ fontSize: 16, color: colors.amber[600] }} />,
    other: <InfoOutlinedIcon sx={{ fontSize: 16, color: colors.gray[500] }} />,
  };

  return <>{icons[type]}</>;
};

// ============================================================================
// Output Card Component
// ============================================================================

interface OutputCardProps {
  output: OutputEntry;
  onNavigateToStep?: (stepId: string) => void;
}

const OutputCard: React.FC<OutputCardProps> = ({ output, onNavigateToStep }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const hasMultipleValues = Object.keys(output.data).length > 2;
  const hasRawOutput = output.rawOutput !== undefined;

  // Render data value based on type
  const renderValue = (value: unknown, key?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return <Typography variant="caption" sx={{ color: colors.gray[400] }}>—</Typography>;
    }

    // Handle sources array (special case for research)
    if (key === 'sources' && Array.isArray(value)) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
          {(value as Array<{ url: string; title?: string }>).slice(0, expanded ? undefined : 3).map((src, idx) => (
            <Box component="li" key={idx} sx={{ mb: 0.25 }}>
              <Link
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.75rem', color: colors.blue[600], display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {src.title || src.url}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Link>
            </Box>
          ))}
          {!expanded && (value as Array<unknown>).length > 3 && (
            <Typography variant="caption" sx={{ color: colors.gray[500] }}>
              +{(value as Array<unknown>).length - 3} more
            </Typography>
          )}
        </Box>
      );
    }

    // Handle keyPoints array
    if (key === 'keyPoints' && Array.isArray(value)) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {(value as string[]).slice(0, expanded ? undefined : 3).map((point, idx) => (
            <Typography
              component="li"
              key={idx}
              variant="caption"
              sx={{ color: colors.gray[600], mb: 0.25 }}
            >
              {point}
            </Typography>
          ))}
          {!expanded && (value as Array<unknown>).length > 3 && (
            <Typography variant="caption" sx={{ color: colors.gray[500] }}>
              +{(value as Array<unknown>).length - 3} more
            </Typography>
          )}
        </Box>
      );
    }

    // Handle other arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return <Typography variant="caption" sx={{ color: colors.gray[400] }}>—</Typography>;
      return (
        <Typography variant="caption" sx={{ color: colors.gray[600] }}>
          {value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')}
        </Typography>
      );
    }

    // Handle objects
    if (typeof value === 'object') {
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            bgcolor: colors.gray[50],
            borderRadius: 1,
            fontSize: '0.7rem',
            overflow: 'auto',
            maxHeight: expanded ? 300 : 100,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: colors.gray[600],
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(value, null, 2)}
        </Box>
      );
    }

    // Handle long strings
    if (typeof value === 'string' && value.length > 150 && !expanded) {
      return (
        <Typography variant="caption" sx={{ color: colors.gray[600], lineHeight: 1.4 }}>
          {value.substring(0, 150)}...
        </Typography>
      );
    }

    // Handle normal strings and primitives
    return (
      <Typography
        variant="caption"
        sx={{ color: colors.gray[600], lineHeight: 1.4, whiteSpace: 'pre-wrap' }}
      >
        {String(value)}
      </Typography>
    );
  };

  // Format key for display
  const formatKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  };

  // Get the primary display fields
  const dataEntries = Object.entries(output.data);
  const displayEntries = expanded ? dataEntries : dataEntries.slice(0, 3);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderColor: colors.gray[200],
        '&:hover': { borderColor: colors.gray[300] },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <TypeIcon type={output.type} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: colors.textPrimary,
                cursor: onNavigateToStep ? 'pointer' : 'default',
                '&:hover': onNavigateToStep ? { color: colors.blue[600] } : {},
              }}
              onClick={() => onNavigateToStep?.(output.stepId)}
            >
              {output.stepTitle}
            </Typography>
            <SourceChip source={output.source} />
          </Box>
          <Typography variant="caption" sx={{ color: colors.gray[500] }}>
            {new Date(output.timestamp).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </Box>

      {/* Data display */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {displayEntries.map(([key, value]) => (
          <Box key={key}>
            <Typography
              variant="caption"
              sx={{ color: colors.gray[500], fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}
            >
              {formatKey(key)}
            </Typography>
            {renderValue(value, key)}
          </Box>
        ))}
      </Box>

      {/* Expand/Collapse and Raw Output buttons */}
      {(hasMultipleValues || hasRawOutput) && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, pt: 1, borderTop: `1px solid ${colors.gray[100]}` }}>
          {hasMultipleValues && dataEntries.length > 3 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              startIcon={expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              sx={{ fontSize: '0.7rem', color: colors.gray[600], textTransform: 'none' }}
            >
              {expanded ? 'Show less' : `Show all (${dataEntries.length})`}
            </Button>
          )}
          {hasRawOutput && (
            <Button
              size="small"
              onClick={() => setShowRaw(!showRaw)}
              startIcon={<CodeIcon />}
              sx={{ fontSize: '0.7rem', color: colors.gray[600], textTransform: 'none' }}
            >
              {showRaw ? 'Hide raw' : 'Show raw'}
            </Button>
          )}
        </Box>
      )}

      {/* Raw output display */}
      <Collapse in={showRaw}>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: colors.gray[700],
            borderRadius: 1,
            fontSize: '0.7rem',
            overflow: 'auto',
            maxHeight: 300,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: colors.gray[100],
            fontFamily: 'monospace',
          }}
        >
          {typeof output.rawOutput === 'string'
            ? output.rawOutput
            : JSON.stringify(output.rawOutput, null, 2)}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ============================================================================
// Phase Group Component
// ============================================================================

interface PhaseGroupProps {
  phaseTitle: string;
  outputs: OutputEntry[];
  defaultExpanded?: boolean;
  onNavigateToStep?: (stepId: string) => void;
}

const PhaseGroup: React.FC<PhaseGroupProps> = ({
  phaseTitle,
  outputs,
  defaultExpanded = true,
  onNavigateToStep,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Phase header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
        }}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
          )}
        </IconButton>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: colors.blue[500],
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: colors.textPrimary,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
          }}
        >
          {phaseTitle}
        </Typography>
        <Chip
          label={`${outputs.length} output${outputs.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: colors.gray[100],
            color: colors.gray[600],
          }}
        />
      </Box>

      {/* Outputs */}
      <Collapse in={expanded}>
        <Box sx={{ pl: 4 }}>
          {outputs.map((output) => (
            <OutputCard
              key={output.id}
              output={output}
              onNavigateToStep={onNavigateToStep}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const OutputsPane: React.FC<OutputsPaneProps> = ({
  plan,
  phases,
  onNavigateToStep,
}) => {
  const [groupBy, setGroupBy] = useState<GroupByValue>('phase');
  const [filterValue, setFilterValue] = useState<FilterValue>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Aggregate and filter outputs
  const outputs = useMemo(() => {
    let result = aggregatePlanOutputs(plan);

    // Apply source/type filter
    if (filterValue !== 'all') {
      // Check if it's a source or type
      if (['automation', 'manual', 'intake', 'assessment'].includes(filterValue)) {
        result = filterOutputsBySource(result, [filterValue as OutputSource]);
      } else {
        result = filterOutputsByType(result, [filterValue as OutputType]);
      }
    }

    // Apply search
    if (searchQuery.trim()) {
      result = searchOutputs(result, searchQuery);
    }

    return result;
  }, [plan, filterValue, searchQuery]);

  // Get stats
  const stats = useMemo(() => getOutputStats(aggregatePlanOutputs(plan)), [plan]);

  // Group outputs
  const groupedOutputs = useMemo(() => {
    if (groupBy === 'phase') {
      return groupOutputsByPhase(outputs);
    }
    // For other groupings, we'll handle inline
    return null;
  }, [outputs, groupBy]);

  // Empty state
  if (stats.total === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 48, color: colors.gray[300], mb: 2 }} />
        <Typography variant="h6" sx={{ color: colors.gray[500], mb: 1 }}>
          No outputs yet
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], textAlign: 'center', maxWidth: 400 }}>
          Outputs will appear here as you complete steps or run automated tasks.
          Each step's collected information will be aggregated for easy review.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ color: colors.gray[600], fontWeight: 500 }}>
          {stats.total} output{stats.total !== 1 ? 's' : ''} collected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {stats.bySource.automation > 0 && (
            <Chip
              icon={<SmartToyIcon sx={{ fontSize: '12px !important' }} />}
              label={`${stats.bySource.automation} auto`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: colors.blue[50], color: colors.blue[700] }}
            />
          )}
          {stats.bySource.manual > 0 && (
            <Chip
              icon={<PersonIcon sx={{ fontSize: '12px !important' }} />}
              label={`${stats.bySource.manual} manual`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: colors.green[50], color: colors.green[700] }}
            />
          )}
          {stats.bySource.intake > 0 && (
            <Chip
              icon={<QuestionAnswerIcon sx={{ fontSize: '12px !important' }} />}
              label={`${stats.bySource.intake} intake`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: colors.purple[50], color: colors.purple[600] }}
            />
          )}
        </Box>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {/* Group by */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupByValue)}
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="phase">Group by Phase</MenuItem>
            <MenuItem value="step">Group by Step</MenuItem>
            <MenuItem value="type">Group by Type</MenuItem>
          </Select>
        </FormControl>

        {/* Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value as FilterValue)}
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="all">All outputs</MenuItem>
            <MenuItem value="automation">Automation only</MenuItem>
            <MenuItem value="manual">Manual only</MenuItem>
            <MenuItem value="intake">Intake only</MenuItem>
            <MenuItem value="research">Research</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="schedule">Schedule</MenuItem>
          </Select>
        </FormControl>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search outputs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* No results after filtering */}
      {outputs.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.gray[500] }}>
            No outputs match your filters
          </Typography>
          <Button
            size="small"
            onClick={() => {
              setFilterValue('all');
              setSearchQuery('');
            }}
            sx={{ mt: 1 }}
          >
            Clear filters
          </Button>
        </Box>
      )}

      {/* Grouped outputs */}
      {groupBy === 'phase' && groupedOutputs && (
        <>
          {Array.from(groupedOutputs.entries())
            .sort(([, a], [, b]) => a.phaseOrder - b.phaseOrder)
            .map(([phaseId, { phaseTitle, outputs: phaseOutputs }]) => (
              <PhaseGroup
                key={phaseId}
                phaseTitle={phaseTitle}
                outputs={phaseOutputs}
                onNavigateToStep={onNavigateToStep}
              />
            ))}
        </>
      )}

      {/* Flat list for step/type grouping */}
      {(groupBy === 'step' || groupBy === 'type') && (
        <Box>
          {outputs.map((output) => (
            <OutputCard
              key={output.id}
              output={output}
              onNavigateToStep={onNavigateToStep}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OutputsPane;

